<!-- ========== 购物车页面 ========== -->
<template>
  <div class="shopping-cart-container">
    <div class="page-header">
      <h1>{{ $t('common.cart') }}</h1>
      <p class="subtitle">共 {{ totalItems }} 件商品</p>
    </div>
    <div v-if="cartItems.length > 0" class="cart-content">
      <!-- 商家分组 -->
      <div v-for="seller in groupedBySeller" :key="seller.sellerId" class="seller-group">
        <div class="seller-header">
          <input type="checkbox" class="checkbox" :checked="isSellerSelected(seller.sellerId)" @change="toggleSellerSelect(seller.sellerId)" />
          <span class="seller-name">{{ seller.customerName }}</span>
          <span class="item-count">({{ seller.items.length }} 件)</span>
          <button class="btn-remove-seller" @click="removeSellerItems(seller.sellerId)">
            移除
          </button>
        </div>
        <table class="cart-table">
          <thead>
            <tr>
              <th style="width: 40px;"></th>
              <th>{{ $t('ShoppingCart.Index.line23_text') }}</th>
              <th style="width: 100px;">{{ $t('ShoppingCart.Index.line24_text') }}</th>
              <th style="width: 120px;">{{ $t('ShoppingCart.Index.line25_text') }}</th>
              <th style="width: 100px;">{{ $t('ShoppingCart.Index.line26_text') }}</th>
              <th style="width: 60px;">{{ $t('ShoppingCart.Index.line27_text') }}</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="item in seller.items" :key="item.id" class="cart-item">
              <td>
                <input type="checkbox" class="checkbox" :checked="isItemSelected(item.id)" @change="toggleItemSelect(item.id)" />
              </td>
              <td>
                <div class="product-info">
                  <img :src="getImageUrl(item.productImage)" :alt="item.productName" class="product-image">
                  <div class="product-details">
                    <h4 class="product-name">{{ item.productName }}</h4>
                    <p class="product-code">{{ item.productCode }}</p>
                  </div>
                </div>
              </td>
              <td>
                <span class="unit-price">¥{{ item.unitPrice }}</span>
              </td>
              <td>
                <div class="quantity-control">
                  <button class="btn-qty" @click="decreaseQuantity(item.id)" :disabled="item.quantity <= 1">
                    -
                  </button>
                  <input v-model.number="item.quantity" type="number" min="1" class="qty-input" @change="updateQuantity(item.id, item.quantity)" />
                  <button class="btn-qty" @click="increaseQuantity(item.id)">+</button>
                </div>
              </td>
              <td>
                <span class="subtotal">¥{{ (item.unitPrice * item.quantity).toFixed(2) }}</span>
              </td>
              <td>
                <button class="btn-delete" @click="removeItem(item.id)" :title="$t('common.delete')">
                  <i class="icon-trash"></i>
                </button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
      <!-- 结算部分 -->
      <div class="cart-footer">
        <div class="footer-left">
          <input type="checkbox" class="checkbox" :checked="allSelected" @change="toggleSelectAll" />
          <span class="select-all-text">{{ $t('ShoppingCart.Index.line72_text') }}</span>
          <button class="btn-delete-selected" @click="deleteSelected" v-if="selectedItems.length > 0">
            删除选中 ({{ selectedItems.length }})
          </button>
        </div>
        <div class="footer-right">
          <div class="price-summary">
            <div class="summary-item">
              <span>{{ $t('ShoppingCart.Index.line80_text') }}</span>
              <span class="price">¥{{ subtotal }}</span>
            </div>
            <div class="summary-item">
              <span>{{ $t('ShoppingCart.Index.line84_text') }}</span>
              <span class="price discount">-¥{{ discount }}</span>
            </div>
            <div class="summary-item total">
              <span>{{ $t('ShoppingCart.Index.line88_text') }}</span>
              <span class="price">¥{{ total }}</span>
            </div>
          </div>
          <div class="action-buttons">
            <router-link to="/seller" class="btn btn-secondary">
              继续采购
            </router-link>
            <button class="btn btn-primary" @click="createOrder" v-if="selectedItems.length > 0">
              创建采购单 ({{ selectedItems.length }} 件)
            </button>
            <button class="btn btn-primary" disabled v-else>
              请选择商品
            </button>
          </div>
        </div>
      </div>
    </div>
    <!-- 空购物车 -->
    <div v-else class="empty-cart">
      <div class="empty-icon">
        <i class="icon-shopping-cart"></i>
      </div>
      <h2>{{ $t('ShoppingCart.Index.line111_text') }}</h2>
      <p>{{ $t('ShoppingCart.Index.line112_text') }}</p>
      <router-link to="/seller" class="btn btn-primary">
        继续采购
      </router-link>
    </div>
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';
import { getImageUrl } from '@/utils/image';

export default {
  name: 'ShoppingCart',
  data() {
    return {
      cartItems: [],
      selectedItems: [],
      loading: false
    };
  },
  computed: {
    ...mapGetters('cart', ['getCartItems']),
    totalItems() {
      return this.cartItems.length;
    },
    groupedBySeller() {
      const grouped = {};
      this.cartItems.forEach(item => {
        if (!grouped[item.sellerId]) {
          grouped[item.sellerId] = {
            sellerId: item.sellerId,
            customerName: item.sellerShopName,
            items: []
          };
        }
        grouped[item.sellerId].items.push(item);
      });
      return Object.values(grouped);
    },
    subtotal() {
      return this.cartItems
        .filter(item => this.selectedItems.includes(item.id))
        .reduce((sum, item) => sum + item.unitPrice * item.quantity, 0)
        .toFixed(2);
    },
    discount() {
      return '0.00';
    },
    total() {
      return (parseFloat(this.subtotal) - parseFloat(this.discount)).toFixed(2);
    },
    allSelected() {
      return this.selectedItems.length === this.cartItems.length && this.cartItems.length > 0;
    }
  },
  methods: {
    getImageUrl,
    ...mapActions('cart', [
      'getCart',
      'updateCartItem',
      'removeCartItem',
      'createOrderFromCart'
    ]),
    async loadCart() {
      this.loading = true;
      try {
        this.cartItems = await this.getCart();
      } catch (error) {
        this.$message.error('加载购物车失败');
      } finally {
        this.loading = false;
      }
    },
    toggleItemSelect(itemId) {
      const index = this.selectedItems.indexOf(itemId);
      if (index > -1) {
        this.selectedItems.splice(index, 1);
      } else {
        this.selectedItems.push(itemId);
      }
    },
    isItemSelected(itemId) {
      return this.selectedItems.includes(itemId);
    },
    toggleSellerSelect(sellerId) {
      const sellerItems = this.cartItems
        .filter(item => item.sellerId === sellerId)
        .map(item => item.id);
      const allSelected = sellerItems.every(id => this.selectedItems.includes(id));
      if (allSelected) {
        this.selectedItems = this.selectedItems.filter(id => !sellerItems.includes(id));
      } else {
        sellerItems.forEach(id => {
          if (!this.selectedItems.includes(id)) {
            this.selectedItems.push(id);
          }
        });
      }
    },
    isSellerSelected(sellerId) {
      const sellerItems = this.cartItems
        .filter(item => item.sellerId === sellerId)
        .map(item => item.id);
      return sellerItems.every(id => this.selectedItems.includes(id)) && sellerItems.length > 0;
    },
    toggleSelectAll() {
      if (this.allSelected) {
        this.selectedItems = [];
      } else {
        this.selectedItems = this.cartItems.map(item => item.id);
      }
    },
    increaseQuantity(itemId) {
      const item = this.cartItems.find(i => i.id === itemId);
      if (item) {
        item.quantity++;
        this.updateQuantity(itemId, item.quantity);
      }
    },
    decreaseQuantity(itemId) {
      const item = this.cartItems.find(i => i.id === itemId);
      if (item && item.quantity > 1) {
        item.quantity--;
        this.updateQuantity(itemId, item.quantity);
      }
    },
    async updateQuantity(itemId, quantity) {
      try {
        await this.updateCartItem({ itemId, quantity });
        this.$message.success('已更新数量');
      } catch (error) {
        this.$message.error('更新数量失败');
      }
    },
    async removeItem(itemId) {
      try {
        await this.removeCartItem(itemId);
        this.cartItems = this.cartItems.filter(item => item.id !== itemId);
        this.selectedItems = this.selectedItems.filter(id => id !== itemId);
        this.$message.success('已从购物车删除');
      } catch (error) {
        this.$message.error('删除失败');
      }
    },
    async removeSellerItems(sellerId) {
      if (!confirm('确定要移除该商家的所有商品吗？')) {
        return;
      }
      const itemIds = this.cartItems
        .filter(item => item.sellerId === sellerId)
        .map(item => item.id);
      for (const itemId of itemIds) {
        await this.removeItem(itemId);
      }
    },
    async deleteSelected() {
      if (!confirm(`确定要删除选中的 ${this.selectedItems.length} 件商品吗？`)) {
        return;
      }
      for (const itemId of this.selectedItems) {
        await this.removeItem(itemId);
      }
    },
    async createOrder() {
      try {
        const selectedCartItems = this.cartItems.filter(item => this.selectedItems.includes(item.id));
        // 按商家分组
        const groupedBySellerForOrder = {};
        selectedCartItems.forEach(item => {
          if (!groupedBySellerForOrder[item.sellerId]) {
            groupedBySellerForOrder[item.sellerId] = {
              sellerId: item.sellerId,
              items: []
            };
          }
          groupedBySellerForOrder[item.sellerId].items.push(item);
        });
        // 如果只有一个商家，直接跳转到创建订单页面
        if (Object.keys(groupedBySellerForOrder).length === 1) {
          const sellerId = Object.keys(groupedBySellerForOrder)[0];
          this.$router.push({
            path: '/purchase-order/create',
            query: { sellerId, fromCart: true }
          });
        } else {
          // 多个商家，提示用户需要分别创建订单
          this.$message.info('不同商家的商品需要分别创建采购单，请按商家选择');
        }
      } catch (error) {
        this.$message.error('创建订单失败');
      }
    }
  },
  mounted() {
    this.loadCart();
  }
};
</script>

<style scoped lang="scss">
.shopping-cart-container {
  padding: 20px;
  
  .page-header {
    margin-bottom: 30px;
    
    h1 {
      font-size: 28px;
      font-weight: bold;
      color: #2c3e50;
      margin-bottom: 8px;
    }
    
    .subtitle {
      color: #999;
      font-size: 14px;
      margin: 0;
    }
  }
  
  .cart-content {
    display: flex;
    flex-direction: column;
    gap: 20px;
    
    .seller-group {
      background: white;
      border-radius: 8px;
      overflow: hidden;
      box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
      
      .seller-header {
        display: flex;
        align-items: center;
        gap: 12px;
        padding: 16px 20px;
        background-color: #f5f7fa;
        border-bottom: 1px solid #eee;
        
        .checkbox {
          width: 18px;
          height: 18px;
          cursor: pointer;
        }
        
        .seller-name {
          font-weight: 500;
          color: #2c3e50;
          font-size: 14px;
        }
        
        .item-count {
          color: #999;
          font-size: 12px;
        }
        
        .btn-remove-seller {
          margin-left: auto;
          background: none;
          border: none;
          color: #e74c3c;
          cursor: pointer;
          font-size: 13px;
          
          &:hover {
            text-decoration: underline;
          }
        }
      }
      
      .cart-table {
        width: 100%;
        border-collapse: collapse;
        
        thead {
          tr {
            background-color: #f5f7fa;
            border-bottom: 1px solid #eee;
            
            th {
              padding: 12px 16px;
              text-align: left;
              font-size: 12px;
              font-weight: 600;
              color: #666;
            }
          }
        }
        
        tbody {
          .cart-item {
            border-bottom: 1px solid #eee;
            transition: background-color 0.3s ease;
            
            &:hover {
              background-color: #f9f9f9;
            }
            
            td {
              padding: 16px;
              font-size: 13px;
              vertical-align: middle;
              
              .checkbox {
                width: 18px;
                height: 18px;
                cursor: pointer;
              }
              
              .product-info {
                display: flex;
                gap: 12px;
                
                .product-image {
                  width: 60px;
                  height: 60px;
                  border-radius: 4px;
                  object-fit: cover;
                  background-color: #f5f7fa;
                }
                
                .product-details {
                  flex: 1;
                  
                  .product-name {
                    margin: 0;
                    font-size: 13px;
                    font-weight: 500;
                    color: #2c3e50;
                    margin-bottom: 4px;
                  }
                  
                  .product-code {
                    margin: 0;
                    font-size: 12px;
                    color: #999;
                  }
                }
              }
              
              .unit-price {
                color: #3498db;
                font-weight: 500;
              }
              
              .quantity-control {
                display: flex;
                align-items: center;
                gap: 8px;
                
                .btn-qty {
                  width: 30px;
                  height: 30px;
                  border: 1px solid #ddd;
                  background: white;
                  border-radius: 4px;
                  cursor: pointer;
                  font-size: 14px;
                  font-weight: bold;
                  transition: all 0.3s ease;
                  
                  &:hover:not(:disabled) {
                    border-color: #3498db;
                    color: #3498db;
                  }
                  
                  &:disabled {
                    cursor: not-allowed;
                    color: #ddd;
                  }
                }
                
                .qty-input {
                  width: 50px;
                  height: 30px;
                  border: 1px solid #ddd;
                  border-radius: 4px;
                  text-align: center;
                  font-size: 13px;
                  
                  &:focus {
                    outline: none;
                    border-color: #3498db;
                  }
                }
              }
              
              .subtotal {
                color: #2c3e50;
                font-weight: 500;
              }
              
              .btn-delete {
                background: none;
                border: none;
                color: #e74c3c;
                cursor: pointer;
                font-size: 16px;
                
                &:hover {
                  color: #c0392b;
                }
              }
            }
          }
        }
      }
    }
    
    .cart-footer {
      background: white;
      border-radius: 8px;
      padding: 20px;
      box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
      display: flex;
      justify-content: space-between;
      align-items: center;
      gap: 20px;
      
      @media (max-width: 768px) {
        flex-direction: column;
        align-items: stretch;
      }
      
      .footer-left {
        display: flex;
        align-items: center;
        gap: 12px;
        
        .checkbox {
          width: 18px;
          height: 18px;
          cursor: pointer;
        }
        
        .select-all-text {
          font-size: 13px;
          color: #666;
        }
        
        .btn-delete-selected {
          margin-left: auto;
          background: none;
          border: none;
          color: #e74c3c;
          cursor: pointer;
          font-size: 13px;
          
          &:hover {
            text-decoration: underline;
          }
        }
      }
      
      .footer-right {
        display: flex;
        align-items: center;
        gap: 30px;
        
        @media (max-width: 768px) {
          flex-direction: column;
          align-items: stretch;
        }
        
        .price-summary {
          display: flex;
          flex-direction: column;
          gap: 8px;
          min-width: 200px;
          
          .summary-item {
            display: flex;
            justify-content: space-between;
            font-size: 13px;
            color: #666;
            
            .price {
              color: #2c3e50;
              font-weight: 500;
              
              &.discount {
                color: #e74c3c;
              }
            }
            
            &.total {
              font-size: 16px;
              font-weight: bold;
              color: #2c3e50;
              padding-top: 8px;
              border-top: 1px solid #eee;
              
              .price {
                color: #3498db;
                font-size: 18px;
              }
            }
          }
        }
        
        .action-buttons {
          display: flex;
          gap: 12px;
          min-width: 300px;
          
          @media (max-width: 768px) {
            flex-direction: column;
            min-width: auto;
          }
        }
      }
    }
  }
  
  .empty-cart {
    background: white;
    border-radius: 8px;
    padding: 80px 20px;
    text-align: center;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
    
    .empty-icon {
      font-size: 80px;
      color: #ddd;
      margin-bottom: 20px;
    }
    
    h2 {
      font-size: 24px;
      color: #2c3e50;
      margin-bottom: 8px;
    }
    
    p {
      color: #999;
      margin-bottom: 30px;
    }
  }
}
</style>